import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  emptyText?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

interface PopupPos {
  top: number;
  left: number;
  width: number;
  placement: 'bottom' | 'top';
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = '— выберите —',
  emptyText = 'Ничего не найдено',
  searchable = true,
  disabled = false,
  className,
  id,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<PopupPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q),
    );
  }, [options, query, searchable]);

  // Sync highlight to selected on open / query change
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.value === value);
    setHighlight(idx >= 0 ? idx : 0);
  }, [open, value, filtered]);

  // Compute popup position (and reposition on scroll/resize)
  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const popupHeight = Math.min(320, 64 + filtered.length * 32 + (searchable ? 50 : 0));
    const spaceBelow = window.innerHeight - r.bottom;
    const placement: 'bottom' | 'top' =
      spaceBelow < popupHeight + 16 && r.top > popupHeight + 16 ? 'top' : 'bottom';
    setPos({
      top: placement === 'bottom' ? r.bottom + 4 : r.top - 4,
      left: r.left,
      width: r.width,
      placement,
    });
  }, [filtered.length, searchable]);

  useLayoutEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    function onScroll() {
      updatePos();
    }
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePos]);

  // Outside click + Escape (учитываем popup в портале)
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (open && searchable) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return;
  }, [open, searchable]);

  // Scroll highlight into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }

  function onListKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = filtered[highlight];
      if (o) pick(o.value);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(filtered.length - 1);
    }
  }

  const triggerCls =
    `inline-flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-bg px-3 text-sm ` +
    `transition-colors duration-150 ease-out hover:border-border-strong ` +
    `focus:border-accent focus:outline-none focus:shadow-focus ` +
    `disabled:bg-bg3 disabled:text-text-subtle disabled:cursor-not-allowed ` +
    (open ? 'border-accent shadow-focus ' : '') +
    (className ?? '');

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? 'truncate text-text' : 'truncate text-text-subtle'}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: pos.placement === 'bottom' ? pos.top : undefined,
              bottom: pos.placement === 'top' ? window.innerHeight - pos.top : undefined,
              left: pos.left,
              width: pos.width,
              minWidth: 200,
              zIndex: 60,
            }}
            className="overflow-hidden rounded-lg border border-border bg-bg shadow-lg"
            onKeyDown={onListKey}
          >
            {searchable && (
              <div className="border-b border-border-subtle p-2">
                <div className="relative">
                  <SearchIcon />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Поиск…"
                    className="block h-8 w-full rounded-md border border-border bg-bg pl-8 pr-3 text-sm text-text placeholder:text-text-subtle outline-none transition-colors duration-150 focus:border-accent focus:shadow-focus"
                  />
                </div>
              </div>
            )}
            <div ref={listRef} role="listbox" className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-text2">{emptyText}</div>
              ) : (
                filtered.map((o, i) => {
                  const isSelected = o.value === value;
                  const isHighlighted = i === highlight;
                  let cls =
                    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors duration-100';
                  if (isHighlighted) cls += ' bg-bg3';
                  if (isSelected) cls += ' text-accent font-semibold';
                  else cls += ' text-text';
                  return (
                    <button
                      type="button"
                      key={o.value}
                      role="option"
                      aria-selected={isSelected}
                      data-idx={i}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(o.value)}
                      className={cls}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.hint && <span className="truncate text-xs text-text2">{o.hint}</span>}
                      {isSelected && !o.hint && <CheckIcon />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`shrink-0 text-text-subtle transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-accent"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
